import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Instagram, ArrowRight,
  Wand2, Clock, Zap, CheckCircle,
  Image, FileText, Palette, Star
} from 'lucide-react';

/**
 * Card de Opção de Criação
 */
const CreationOptionCard = ({ 
  icon: Icon, 
  title, 
  description, 
  features, 
  buttonText, 
  onClick, 
  isPrimary = false,
  badge = null,
  gradient
}) => (
  <div 
    className={`
      relative bg-white rounded-2xl border-2 p-8 transition-all duration-300
      hover:shadow-xl hover:scale-[1.02] cursor-pointer group
      ${isPrimary ? 'border-amber-400 shadow-lg' : 'border-gray-200 hover:border-amber-200'}
    `}
    onClick={onClick}
  >
    {/* Badge */}
    {badge && (
      <div className="absolute -top-3 right-6 px-4 py-1.5 rounded-xl text-xs font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white flex items-center gap-1.5">
        <Star className="w-3 h-3" />
        {badge}
      </div>
    )}

    {/* Ícone */}
    <div 
      className={`
        w-16 h-16 rounded-2xl flex items-center justify-center mb-6
        ${gradient || 'bg-gray-100'}
      `}
    >
      <Icon className={`w-8 h-8 ${isPrimary ? 'text-white' : 'text-gray-700'}`} />
    </div>

    {/* Título */}
    <h3 className="text-2xl font-bold text-gray-900 mb-3">
      {title}
    </h3>

    {/* Descrição */}
    <p className="text-gray-600 mb-6 leading-relaxed">
      {description}
    </p>

    {/* Features */}
    <ul className="space-y-3 mb-8">
      {features.map((feature, idx) => (
        <li key={idx} className="flex items-center gap-3 text-sm text-gray-700">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          {feature}
        </li>
      ))}
    </ul>

    {/* Botão */}
    <button
      className={`
        w-full py-4 px-6 rounded-xl font-semibold text-base
        flex items-center justify-center gap-2 transition-all
        ${isPrimary 
          ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:from-amber-500 hover:to-orange-600 shadow-md' 
          : 'bg-gray-100 text-gray-900 hover:bg-gradient-to-r hover:from-amber-400 hover:to-orange-500 hover:text-white'
        }
      `}
    >
      {buttonText}
      <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
    </button>
  </div>
);

/**
 * Componente Principal - Seletor de Tipo de Criação
 */
const ContentCreationSelector = () => {
  const navigate = useNavigate();

  const handleManualFlow = () => {
    navigate('/contents/new/manual');
  };

  const handleInstagramFlow = () => {
    navigate('/contents/new/instagram');
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl text-sm font-medium text-amber-800 mb-6">
            <Sparkles className="w-4 h-4" />
            Criação de Conteúdos com IA
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Como você quer criar?
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Escolha a forma que melhor se adapta às suas necessidades
          </p>
        </div>

        {/* Cards de Opções */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Opção Instagram */}
          <CreationOptionCard
            icon={Instagram}
            title="Via Instagram"
            description="Digite apenas seu @ e deixe a IA analisar seu perfil para criar conteúdos personalizados automaticamente."
            features={[
              "Análise automática do perfil",
              "Extração de cores da marca",
              "Bio transformada em briefing",
              "Criação em 1 clique"
            ]}
            buttonText="Começar com Instagram"
            onClick={handleInstagramFlow}
            isPrimary={true}
            badge="NOVO"
            gradient="bg-gradient-to-br from-amber-400 to-orange-500"
          />

          {/* Opção Manual */}
          <CreationOptionCard
            icon={Wand2}
            title="Criação Manual"
            description="Tenha controle total sobre cada detalhe do seu conteúdo com nosso wizard completo."
            features={[
              "Personalização completa",
              "Escolha de cores e estilos",
              "Briefing detalhado",
              "Múltiplos tipos de conteúdo"
            ]}
            buttonText="Criar Manualmente"
            onClick={handleManualFlow}
            isPrimary={false}
            gradient="bg-gray-100"
          />
        </div>

        {/* Dica */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-xl text-sm text-amber-800">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span><strong>Dica:</strong> Se é sua primeira vez, recomendamos começar com o fluxo Instagram para ver a mágica acontecer!</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentCreationSelector;